/**
 * Light Mode Theme Palette
 * Matches Lawis Landing Page design — warm whites with gold accent
 */

import { BRAND, FONTS, LIGHT, STATUS } from '../design-system/tokens';

export const lightThemePalette = {
  // Page Structure
  background: {
    default: LIGHT.bg,              // #ffffff
    paper: LIGHT.surface,           // #ffffff
    navbar: LIGHT.bgNavbar,         // #fafaf9
  },

  // Primary — Lawis Gold
  primary: {
    main: BRAND.gold,               // #c9a96e
    light: BRAND.goldLight,         // #d4b97e
    dark: BRAND.goldDark,           // #b8955a
    contrastText: '#ffffff',
  },

  // Secondary
  secondary: {
    main: STATUS.info,              // #6366F1
    light: STATUS.infoLight,        // #818cf8
    dark: '#4f46e5',
    contrastText: '#ffffff',
  },

  // Status Colors
  success: {
    main: STATUS.success,           // #52c41a
    light: STATUS.successLight,
    dark: '#3da014',
  },
  warning: {
    main: STATUS.warning,           // #F59E0B
    light: STATUS.warningLight,
    dark: '#d97706',
  },
  error: {
    main: STATUS.error,             // #EC4899
    light: STATUS.errorLight,
    dark: '#db2777',
  },
  info: {
    main: STATUS.info,              // #6366F1
    light: STATUS.infoLight,
    dark: '#4f46e5',
  },

  // Text Colors
  text: {
    primary: LIGHT.textPrimary,     // #1c1917
    secondary: LIGHT.textSecondary, // #78716c
    disabled: LIGHT.textTertiary,   // #a8a29e
    tertiary: LIGHT.textTertiary,
  },

  // Dividers and Borders
  divider: LIGHT.borderPrimary,     // #e7e5e4
  border: LIGHT.borderSecondary,    // #d6d3d1

  // Neutral Grey Scale (stone tones)
  grey: {
    50: '#fafaf9',
    100: '#f5f5f4',
    200: '#e7e5e4',
    300: '#d6d3d1',
    400: '#a8a29e',
    500: '#78716c',
    600: '#57534e',
    700: '#44403c',
    800: '#292524',
    900: '#1c1917',
  },

  // Action States
  action: {
    active: BRAND.gold,
    hover: LIGHT.surfaceHover,
    selected: 'rgba(201, 169, 110, 0.08)',
    disabled: LIGHT.textTertiary,
    disabledBackground: LIGHT.bgTertiary,
  },

  // Brand Colors
  brand: {
    gold: BRAND.gold,
    goldSubtle: BRAND.goldSubtle,
    goldBorder: BRAND.goldBorder,
  },
};

/**
 * Light Mode Typography Configuration
 */
export const lightTypography = {
  fontFamily: FONTS.body,

  h1: {
    fontFamily: FONTS.display,
    fontSize: '28px',
    fontWeight: 300,
    lineHeight: 1.2,
    letterSpacing: '-0.02em',
  },
  h2: {
    fontFamily: FONTS.display,
    fontSize: '22px',
    fontWeight: 400,
    lineHeight: 1.25,
    letterSpacing: '-0.02em',
  },
  h3: {
    fontFamily: FONTS.display,
    fontSize: '18px',
    fontWeight: 500,
    lineHeight: 1.3,
    letterSpacing: '-0.01em',
  },
  h4: {
    fontFamily: FONTS.body,
    fontSize: '16px',
    fontWeight: 600,
    lineHeight: 1.35,
  },
  h5: {
    fontFamily: FONTS.body,
    fontSize: '15px',
    fontWeight: 600,
    lineHeight: 1.4,
  },
  h6: {
    fontFamily: FONTS.body,
    fontSize: '14px',
    fontWeight: 600,
    lineHeight: 1.4,
  },
  body1: {
    fontFamily: FONTS.body,
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: 1.6,
  },
  body2: {
    fontFamily: FONTS.body,
    fontSize: '13px',
    fontWeight: 400,
    lineHeight: 1.5,
  },
  caption: {
    fontFamily: FONTS.body,
    fontSize: '12px',
    fontWeight: 500,
    lineHeight: 1.4,
    letterSpacing: '0.03em',
  },
  subtitle1: {
    fontFamily: FONTS.body,
    fontSize: '15px',
    fontWeight: 500,
    lineHeight: 1.5,
  },
  subtitle2: {
    fontFamily: FONTS.body,
    fontSize: '13px',
    fontWeight: 500,
    lineHeight: 1.5,
  },
};

/**
 * Light Mode Component Overrides
 */
export const lightComponentOverrides = {
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: '12px',
        border: `1px solid ${LIGHT.borderPrimary}`,
        backgroundColor: LIGHT.surface,
        boxShadow: LIGHT.shadowCard,
        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          borderColor: LIGHT.borderSecondary,
          boxShadow: LIGHT.shadowElevated,
        },
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        backgroundColor: LIGHT.surface,
        backgroundImage: 'none',
        borderColor: LIGHT.borderPrimary,
      },
      elevation0: {
        boxShadow: 'none',
      },
    },
  },
  MuiDialog: {
    styleOverrides: {
      paper: {
        backgroundColor: LIGHT.surface,
        backgroundImage: 'none',
        border: `1px solid ${LIGHT.borderPrimary}`,
        borderRadius: '12px',
        boxShadow: LIGHT.shadowElevated,
      },
    },
  },
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: '8px',
        textTransform: 'none',
        fontWeight: 500,
        fontFamily: FONTS.body,
        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
      },
      containedPrimary: {
        backgroundColor: BRAND.gold,
        color: '#ffffff',
        '&:hover': {
          backgroundColor: BRAND.goldDark,
        },
      },
      containedSecondary: {
        backgroundColor: STATUS.info,
        color: '#ffffff',
        '&:hover': {
          backgroundColor: '#4f46e5',
        },
      },
      outlined: {
        borderColor: LIGHT.borderPrimary,
        color: LIGHT.textPrimary,
        '&:hover': {
          backgroundColor: LIGHT.surfaceHover,
          borderColor: LIGHT.borderSecondary,
        },
      },
    },
  },
  MuiTextField: {
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-root': {
          borderRadius: '8px',
          color: LIGHT.textPrimary,
          '& fieldset': {
            borderColor: LIGHT.borderPrimary,
          },
          '&:hover fieldset': {
            borderColor: LIGHT.borderSecondary,
          },
          '&.Mui-focused fieldset': {
            borderColor: BRAND.gold,
          },
        },
      },
    },
  },
  MuiListItemButton: {
    styleOverrides: {
      root: {
        borderRadius: '8px',
        transition: 'all 0.2s ease',
        '&.Mui-selected': {
          backgroundColor: BRAND.goldSubtle,
          '&:hover': {
            backgroundColor: 'rgba(201, 169, 110, 0.16)',
          },
        },
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: '6px',
        fontFamily: FONTS.body,
        fontWeight: 500,
      },
    },
  },
  MuiPopover: {
    styleOverrides: {
      paper: {
        backgroundColor: LIGHT.surface,
        backgroundImage: 'none',
        border: `1px solid ${LIGHT.borderPrimary}`,
        boxShadow: LIGHT.shadowElevated,
      },
    },
  },
  MuiDrawer: {
    styleOverrides: {
      paper: {
        backgroundColor: LIGHT.surface,
        backgroundImage: 'none',
        borderColor: LIGHT.borderPrimary,
      },
    },
  },
  MuiMenu: {
    styleOverrides: {
      paper: {
        backgroundColor: LIGHT.surface,
        backgroundImage: 'none',
        border: `1px solid ${LIGHT.borderPrimary}`,
        boxShadow: LIGHT.shadowElevated,
      },
    },
  },
  MuiPickersPopper: {
    styleOverrides: {
      paper: {
        backgroundColor: LIGHT.surface,
        backgroundImage: 'none',
        border: `1px solid ${LIGHT.borderPrimary}`,
        borderRadius: '12px',
        boxShadow: LIGHT.shadowElevated,
        color: LIGHT.textPrimary,
      },
    },
  },
  MuiDateCalendar: {
    styleOverrides: {
      root: {
        color: LIGHT.textPrimary,
      },
    },
  },
  MuiPickersCalendarHeader: {
    styleOverrides: {
      root: {
        color: LIGHT.textPrimary,
      },
      label: {
        color: LIGHT.textPrimary,
      },
      switchViewButton: {
        color: LIGHT.textSecondary,
      },
    },
  },
  MuiPickersArrowSwitcher: {
    styleOverrides: {
      button: {
        color: LIGHT.textSecondary,
      },
    },
  },
  MuiDayCalendar: {
    styleOverrides: {
      weekDayLabel: {
        color: LIGHT.textTertiary,
      },
    },
  },
  MuiPickersDay: {
    styleOverrides: {
      root: {
        color: LIGHT.textPrimary,
        '&:hover': {
          backgroundColor: 'rgba(201, 169, 110, 0.12)',
        },
        '&.Mui-selected': {
          backgroundColor: BRAND.gold,
          color: '#ffffff',
          '&:hover': {
            backgroundColor: BRAND.goldDark,
          },
          '&:focus': {
            backgroundColor: BRAND.gold,
          },
        },
        '&.MuiPickersDay-today': {
          borderColor: BRAND.gold,
        },
      },
    },
  },
  MuiPickersYear: {
    styleOverrides: {
      yearButton: {
        color: LIGHT.textPrimary,
        '&:hover': {
          backgroundColor: 'rgba(201, 169, 110, 0.12)',
        },
        '&.Mui-selected': {
          backgroundColor: BRAND.gold,
          color: '#ffffff',
        },
      },
    },
  },
  MuiPickersMonth: {
    styleOverrides: {
      monthButton: {
        color: LIGHT.textPrimary,
        '&:hover': {
          backgroundColor: 'rgba(201, 169, 110, 0.12)',
        },
        '&.Mui-selected': {
          backgroundColor: BRAND.gold,
          color: '#ffffff',
        },
      },
    },
  },
};
